import {
  archivedToolCategoriesQuery,
  toolCategoriesQuery,
  useRestoreToolCategoryMutation,
} from "@common/api/tool-categories"
import type { ToolCategoryPublic } from "@common/client"
import { DropdownMenuItem } from "@common/components/ui/dropdown-menu"
import { useHasPermission } from "@common/hooks/use-permissions"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router"
import { DetailPanel } from "@/components/layout/detail-panel"
import useCustomToast from "@/hooks/use-custom-toast"
import { PanelHeading } from "@/routes/_layout/settings/_panel/-components/panel-heading"
import { PanelMenu } from "@/routes/_layout/settings/_panel/-components/panel-menu"
import { CategoryDetailsForm } from "@/routes/_layout/settings/_panel/workspace/tool-categories/-components/form/category-details-form"
import ArchiveOutline from "~icons/material-symbols/archive-outline"
import UnarchiveOutline from "~icons/material-symbols/unarchive-outline"

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/tool-categories/_list/$categoryId",
)({
  staticData: { detailSidebar: true },
  component: CategoryPanel,
})

function CategoryPanel(): React.ReactElement {
  const { categoryId } = Route.useParams()
  const { archived = false } = Route.useSearch()
  const { data } = useSuspenseQuery(
    archived ? archivedToolCategoriesQuery() : toolCategoriesQuery(),
  )
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const category = data.data.find((item) => item.id === categoryId)

  if (!category) {
    return <Outlet />
  }

  const close = () =>
    navigate({ to: "/settings/workspace/tool-categories", search: true })

  return (
    <DetailPanel
      title={category.name}
      heading={
        <PanelHeading
          label="Tool category"
          action={<CategoryMenu category={category} />}
        />
      }
      onClose={close}
    >
      <CategoryDetailsForm
        key={`${category.id}-${category.deleted_at}`}
        category={category}
        onSave={(saved) =>
          showToast("Saved", `${saved.name} updated.`, "success")
        }
      />
      <Outlet />
    </DetailPanel>
  )
}

function CategoryMenu({
  category,
}: {
  category: ToolCategoryPublic
}): React.ReactNode {
  const canDelete = useHasPermission("tools:delete")
  const canRestore = useHasPermission("tools:update")
  const restore = useRestoreToolCategoryMutation()
  const navigate = useNavigate()
  const showToast = useCustomToast()

  const restoreAction = canRestore && (
    <DropdownMenuItem
      disabled={restore.isPending}
      onSelect={() =>
        restore.mutate(
          { category_id: category.id },
          {
            onSuccess: (restored) => {
              showToast("Restored", `${restored.name} restored.`, "success")
              navigate({
                to: "/settings/workspace/tool-categories/$categoryId",
                params: { categoryId: restored.id },
                search: (previous) => ({ ...previous, archived: undefined }),
              })
            },
            onError: (error) => handleError(error, showToast),
          },
        )
      }
    >
      <UnarchiveOutline />
      Restore
    </DropdownMenuItem>
  )
  const archive = canDelete && (
    <DropdownMenuItem asChild>
      <Link
        to="/settings/workspace/tool-categories/$categoryId/delete"
        params={{ categoryId: category.id }}
        search={true}
      >
        <ArchiveOutline />
        Archive
      </Link>
    </DropdownMenuItem>
  )
  const action = category.deleted_at ? restoreAction : archive

  return action && <PanelMenu label="Tool category actions">{action}</PanelMenu>
}
